/**
 * QuestionAnswerer — question display + answer form for the guardian. The recipient panel, the mutual challenge and the
 * forensic-recovery lift all render it, so the answer inputs turn spell-check, autocorrect and autofill off in one place
 * (`SECRET_INPUT_PROPS`).
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { SECRET_INPUT_PROPS } from "@/lib/secret-input-props";

interface Question {
  questionOrder: number;
  questionText: string;
}

interface QuestionAnswererProps {
  questions: Question[];
  onSubmit: (answers: string[]) => void;
  /**
   * When true, disables the inputs + submit button so an in-flight submission
   * can't be re-fired (a double-click would re-run keygen/store/POST and burn an
   * attempt). Backward-compatible default false. Mirrors QuestionCreator's `busy`.
   */
  busy?: boolean;
}

export function QuestionAnswerer({ questions, onSubmit, busy = false }: QuestionAnswererProps): JSX.Element {
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ""));

  function handleChange(idx: number, value: string): void {
    const updated = [...answers];
    updated[idx] = value;
    setAnswers(updated);
  }

  function handleSubmit(): void {
    if (busy) return;
    onSubmit(answers);
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-4"
    >
      <h3 className="text-lg font-semibold">Answer these verification questions</h3>
      {questions.map((q, i) => (
        <div key={q.questionOrder} className="flex flex-col gap-1">
          <label className="font-medium">Q{q.questionOrder}: {q.questionText}</label>
          <input
            type="text"
            {...SECRET_INPUT_PROPS}
            value={answers[i]}
            disabled={busy}
            onChange={(e) => handleChange(i, e.target.value)}
            className="rounded-lg border border-zinc-300 px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed dark:border-zinc-600 dark:bg-zinc-800"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={busy}
        className="rounded-lg bg-zinc-900 px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zinc-100 dark:text-zinc-900"
      >
        {busy ? "Submitting…" : "Submit Answers"}
      </button>
    </motion.div>
  );
}
